// mana-info.ts — client-side render of the creature Mana / Spirit panel, reading
// from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces options/tools/encounter_creatures/encounter_creature_info/
// creature_mana_spirit_info_inc.php. Three parts:
//   - Mana: current mana (encounter_creatures.mana).
//   - Adjust (permission-gated, in-encounter): slider + delta + Adjust button. The
//     button keeps the PHP write endpoint (adjusts/adjust_mana_inc.php); that write
//     runs process_affected_ids() → websocket caster_update, which refreshes the
//     cache and re-renders THIS panel via caster-update.ts → ftRerenderOpenInfoPanel.
//     No full PHP side-panel re-query.
//   - Spirit: feat / inventory / total spirit (the mana cap), read via ftLayerValue.

import { hasPlayerPermission } from '../shared/array-types';
import { setActiveInfoPanel } from './active-panel';
import type { CachedCaster } from './types';

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}
/** PHP display_value_or_dash twin: '—' for 0/empty, else the value. */
function dashIfZero(v: unknown): string {
    const n = num(v);
    return n === 0 ? '—' : String(n);
}

function renderManaSpiritPanel(caster: CachedCaster, casterId: string): string {
    const ecArr = (caster.encounter_creatures ?? {}) as Record<string, unknown>;
    const encounterCreatureId = String(unwrap(ecArr.encounter_creature_id) ?? '').trim();
    const manaCap = Math.max(0, num(ftLayerValue(caster, 'spirit')));
    // Item creatures (no encounter row) have no stored mana — show FULL, matching the
    // mana bar (build_v_mana_ecac_luck_bars_inc.php) + vitality. Encounter creatures use live mana.
    const manaCurrent = encounterCreatureId !== '' ? num(ecArr.mana) : manaCap;

    let html = `
    <table class="table border">
        <thead><tr><th scope="col" colspan="2" class="table-dark align-middle rounded-top">
            <h4 class="text-white">${iconMap('mana')} Mana:</h4></th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">Current ${iconMap('mana')}:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${manaCurrent}</span></h3></td></tr>
        </tbody>
    </table>`;

    if (hasPlayerPermission(caster) && encounterCreatureId !== '') {
        const sliderVal = Math.min(Math.max(0, manaCurrent), manaCap);

        // Slider/delta two-way sync handlers — preserved verbatim from the PHP (vanilla JS).
        const sliderOnInput = "(function(){var b=parseInt(document.getElementById('ec_mana_baseline').value,10)||0;var t=parseInt(document.getElementById('ec_mana_range').value,10)||0;var d=t-b;var h=document.getElementById('input_mana_qty');if(h){h.value=d;}var lbl=document.getElementById('ec_mana_range_label');if(lbl){lbl.textContent=t;}})()";
        const deltaOnInput = "(function(){var b=parseInt(document.getElementById('ec_mana_baseline').value,10)||0;var d=parseInt(document.getElementById('input_mana_qty').value,10);if(isNaN(d))d=0;var r=document.getElementById('ec_mana_range');if(!r)return;var mx=parseInt(r.max,10)||0;var t=Math.min(mx,Math.max(0,b+d));r.value=t;var lbl=document.getElementById('ec_mana_range_label');if(lbl){lbl.textContent=t;}})()";

        // Bind the adjust to THIS panel's creature (cache-sourced encounterCreatureId), not the
        // ambient #caster_id global — the standardized per-creature action path (ftAdjustForCaster,
        // the generalization of adjustHealth's override branch). Fixes the empty-caster failure when
        // you have no home creature or are a GM adjusting someone else's mana.
        const handler =
            "(async()=>{try{"
            // The resource truth seam (websocket/resource-truth.ts ftResourceAdjust) — never a legacy post (N6 2026-10-01).
            + `var o=await window.ftResourceAdjust('mana','${encounterCreatureId}','input_mana_qty');`
            // The truth lane renders from the committed row; the sheet re-query is the LEGACY path's refresh only.
            + `if(o!=='handled'&&o!=='refused')await refreshCasterSheet('${encounterCreatureId}');`
            + "}catch(e){console.error('Error updating mana:',e);}})()";

        html += `
        <div class="p-1 mt-2 mb-2 border rounded">
            <input type="hidden" id="ec_mana_baseline" value="${manaCurrent}">
            <div class="form-group mb-3">
                <h5 class="text-center mb-2">${iconMap('mana')} Set current mana</h5>
                <div class="d-flex align-items-center px-1">
                    <span class="small text-muted mr-2">0</span>
                    <input type="range" id="ec_mana_range" class="form-range flex-grow-1 mx-1" min="0" max="${manaCap}" step="1" value="${sliderVal}" aria-label="Set current mana" oninput="${escapeHtml(sliderOnInput)}">
                    <span class="small text-muted ml-2">${manaCap}</span>
                </div>
                <div class="text-center small text-muted" id="ec_mana_range_label">${sliderVal}</div>
                <p class="small text-muted text-center mb-0">Drag to your target current mana, then tap Adjust below.</p>
            </div>
            <div class="form-group mb-3">
                <h5 class="text-center">Adjust (delta):</h5>
                <input id="input_mana_qty" class="form-control text-center" type="number" value="0" oninput="${escapeHtml(deltaOnInput)}">
            </div>
            <div id="div_mana_spirit" class="my-2 text-center">
                <button class="btn btn-primary" id="btn_increase_mana" onclick="${escapeHtml(handler)}">Adjust ${iconMap('mana')}</button>
            </div>
        </div>`;
    }

    html += `
    <table class="table border">
        <thead><tr><th scope="col" colspan="2" class="table-dark align-middle rounded-top">
            <h4 class="text-white">${iconMap('spirit')} Spirit:</h4></th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">${iconMap('feat')}:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${escapeHtml(dashIfZero(ftLayerValue(caster, 'feat_spirit')))}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">${iconMap('inventory')}:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${escapeHtml(dashIfZero(ftLayerValue(caster, 'inventory_spirit')))}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">Total ${iconMap('spirit')}:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${manaCap}</span></h3></td></tr>
        </tbody>
    </table>`;

    return html;
}

/** Entry point. Repointed onclick: the Mana bar in build_v_mana_ecac_luck_bars_inc.php. */
export async function renderManaSpiritInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderManaSpiritPanel(c, casterId);
    setActiveInfoPanel(casterId, targetId, render);
    target.innerHTML = render(caster);
}
